import { createStyles, CircularProgress, Grid, IconButton, makeStyles, Theme, Typography } from '@material-ui/core';
import { Cancel, CheckCircle, Search } from '@material-ui/icons';
import { booleanOptions, BooleanValue, indexadorRemuneracaoOptions } from 'common/genericOptions';
import { useGenericStyles } from 'common/genericStyles';
import {
  FuncaoParticipante,
  InstrumentoFinanceiroApiResponse,
  PosicaoResponse,
} from 'common/genericTypes';
import { ContaDepositoMask, CpfCnpjMask } from 'components/masks';
import {
  Button,
  DatePicker,
  Dropdown,
  LgSpace,
  MdSpace,
  NumberField,
  RadioButton,
  Section,
  SmSpace,
  TextField,
} from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from 'store';
import { validaContaDeposito, validaCpfCnpj, stripCpfCnpjFormatting } from 'utils/validation';
import moment, { Moment } from 'moment';
import { getQuantityType } from 'utils/instrumentoFinanceiroUtils';
import { buildCompromissadaRequest } from './buildCompromissadaRequest';
import {
  descreverQuantidadeDisponivel,
  encontrarPosicaoDoInvestidor,
  MENSAGEM_DATA_DE_RETORNO_RETROATIVA,
  podeComandarCompromissada,
  resolverErroDaQuantidade,
} from './validacoesCompromissada';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    iconWrapper: {
      width: '112px',
      height: '112px',
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    icon: {
      width: '40px',
      height: '40px',
    },
  })
);



const CadastrarOperacaoCompraVendaCompromissada: React.FC = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const genericClasses = useGenericStyles();
  const history = useHistory();

  const getInstrumentoDetalhesByCodigo = useSelector((state: RootState) => state.instrumentoApi.getInstrumentoDetalhesByCodigo);
  const getPosicoes = useSelector((state: RootState) => state.investidorApi.getPosicoes);
  const getParticipantesApi = useSelector((state: RootState) => state.participanteApi.getParticipantes);
  const postOperacaoCompraVendaCompromissada = useSelector((state: RootState) => state.operacaoApi.postOperacaoCompraVendaCompromissada);
  const custodianteLogado = useSelector((state: RootState) => state.auth.usuario.participante);

  const [posicoes, setPosicoes] = useState<Array<PosicaoResponse>>();
  const [instrumento, setInstrumento] = useState<InstrumentoFinanceiroApiResponse | null>(null);
  const [codigoInstrumento, setCodigoInstrumento] = useState<string>('');
  const [buscandoInstrumento, setBuscandoInstrumento] = useState<boolean>(false);
  const [investidorCpfCnpj, setInvestidorCpfCnpj] = useState<string>('');
  const [investidorCpfCnpjValido, setInvestidorCpfCnpjValido] = useState(false);
  const [custodianteDestinoRazaoSocial, setCustodianteDestinoRazaoSocial] = useState<string>('');
  const [custodianteDestinoId, setCustodianteDestinoId] = useState<string>('');
  const [cnpjCustodianteDestino, setCnpjCustodianteDestino] = useState<string>('');
  const [cnpjCustodianteDestinoValido, setCnpjCustodianteDestinoValido] = useState(false);
  const [buscandoCustodiante, setBuscandoCustodiante] = useState<boolean>(false);
  const [custodianteEncontrado, setCustodianteEncontrado] = useState<boolean>(false);
  const [contaDepositoDestino, setContaDepositoDestino] = useState<string>();
  const [contaDepositoDestinoValido, setContaDepositoDestinoValido] = useState(false);
  const [quantidade, setQuantidade] = useState<number>();
  const [quantidadeMax, setQuantidadeMax] = useState<number>();
  const [dataDeRetorno, setDataRetorno] = useState<Moment | null>();
  const [taxaDeJurosFixo, setTaxaDeJurosFixo] = useState<number>();
  const [indexador, setIndexador] = useState<string>();
  const [precoUnitario, setPrecoUnitario] = useState<number>();
  const [custodiaPropria, setCustodiaPropria] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [finished, setFinished] = useState<boolean>(false);
  const [inadimplido, setInadimplido] = useState<boolean>(false);
  const [indexadorPorcentagem, setPorcentagemIndexador] = useState<number>();

  const buscarInstrumentoPorCodigo = useCallback(async () => {
    if (!codigoInstrumento.trim()) return;
    setBuscandoInstrumento(true);
    try {
      const result = await getInstrumentoDetalhesByCodigo(dispatch, codigoInstrumento.trim(), true);
      setInstrumento(result ?? null);
    } catch (error) {
      setInstrumento(null);
    } finally {
      setBuscandoInstrumento(false);
    }
  }, [dispatch, getInstrumentoDetalhesByCodigo, codigoInstrumento]);

  const buscarCustodiantePorCnpj = useCallback(async () => {
    const cnpjLimpo = stripCpfCnpjFormatting(cnpjCustodianteDestino);
    if (!cnpjLimpo) return;
    setBuscandoCustodiante(true);
    try {
      const result = await getParticipantesApi(dispatch, { cnpj: cnpjLimpo, funcao: FuncaoParticipante.Custodiante, suspenso: false }, true);
      if (result?.length) {
        setCustodianteDestinoRazaoSocial(result[0].razaoSocial ?? '');
        setCustodianteDestinoId(result[0].id ?? '');
        setCustodianteEncontrado(true);
      } else {
        setCustodianteDestinoRazaoSocial('');
        setCustodianteDestinoId('');
        setCustodianteEncontrado(false);
      }
    } catch (error) {
      setCustodianteDestinoRazaoSocial('');
      setCustodianteDestinoId('');
      setCustodianteEncontrado(false);
    } finally {
      setBuscandoCustodiante(false);
    }
  }, [dispatch, getParticipantesApi, cnpjCustodianteDestino]);

  const getInvestidores = useCallback(
    (instrumentoFinanceiroId?: string) => {
      getPosicoes(dispatch, instrumentoFinanceiroId ? { instrumentoFinanceiroId } : undefined, true)
        .then((result) => {
          setPosicoes(result);
        })
        .catch();
    },
    [dispatch, getPosicoes]
  );

  // Buscar investidores quando instrumento mudar
  useEffect(() => {
    if (instrumento) getInvestidores(instrumento.instrumentoFinanceiroGuid);
  }, [instrumento, getInvestidores]);

  // Atualizar quantidade máxima quando investidor ou instrumento mudar
  useEffect(() => {
    const posicaoEncontrada = encontrarPosicaoDoInvestidor(posicoes, investidorCpfCnpj, instrumento?.instrumentoFinanceiroGuid);
    setQuantidadeMax(posicaoEncontrada?.saldoDisponivel);
  }, [investidorCpfCnpj, posicoes, instrumento, setQuantidadeMax]);

  // Efeito 1 — Inadimplência: observa apenas instrumento
  useEffect(() => {
    if (instrumento) {
      setInadimplido(instrumento.hasPagamentosInadimplentes === true);
    }
  }, [instrumento]);

  // Efeito 2 — Custódia própria: quando custodiaPropria = true, seta custodiante logado
  useEffect(() => {
    if (custodiaPropria) {
      setCnpjCustodianteDestino(CpfCnpjMask(custodianteLogado.cnpj ?? ''));
      setCnpjCustodianteDestinoValido(true);
      setCustodianteEncontrado(true);
      setCustodianteDestinoRazaoSocial(custodianteLogado.razaoSocial ?? '');
      setCustodianteDestinoId(custodianteLogado.id ?? '');
    }
  }, [custodiaPropria, custodianteLogado]);

  // Efeito 3 — Reset custódia: quando a custódia não é própria, limpa custodiante
  useEffect(() => {
    if (!custodiaPropria) {
      setCnpjCustodianteDestino('');
      setCnpjCustodianteDestinoValido(false);
      setCustodianteEncontrado(false);
      setCustodianteDestinoRazaoSocial('');
      setCustodianteDestinoId('');
    }
  }, [custodiaPropria]);

  const updateContaDeposito = (value: string) => {
    const result = ContaDepositoMask(value ?? '');
    setContaDepositoDestino(result);
    setContaDepositoDestinoValido(validaContaDeposito(result ?? ''));
  };

  const updateMaskInvestidorCpfCnpj = (value: string) => {
    const result = CpfCnpjMask(value ?? '');
    setInvestidorCpfCnpj(result);
    setInvestidorCpfCnpjValido(validaCpfCnpj(result ?? ''));
  };

  const updateCnpjCustodianteDestino = (value: string) => {
    const result = CpfCnpjMask(value ?? '');
    setCnpjCustodianteDestino(result);
    setCnpjCustodianteDestinoValido(validaCpfCnpj(result ?? ''));
  };

  const hoje = moment();
  const erroDaQuantidade = resolverErroDaQuantidade(quantidade, quantidadeMax);

  const podeComandar = podeComandarCompromissada({
    temInstrumento: !!instrumento,
    investidorCpfCnpjValido: !!investidorCpfCnpj && investidorCpfCnpjValido,
    cnpjCustodianteDestinoValido,
    custodianteEncontrado,
    contaDepositoDestinoValida: !!contaDepositoDestino && contaDepositoDestinoValido,
    quantidade,
    quantidadeDisponivel: quantidadeMax,
    precoUnitario,
    indexador,
    dataDeRetorno,
    hoje,
  });

  const handleSubmit = () => {
    // podeComandar já garante o preenchimento; a segunda condição existe para
    // estreitar os tipos opcionais antes de montar o request.
    if (!podeComandar || !instrumento || !quantidade || !precoUnitario || !contaDepositoDestino) {
      return;
    }

    setSubmitting(true);

    const cpfCnpjLimpo = stripCpfCnpjFormatting(investidorCpfCnpj);
    const investidorOrigem = encontrarPosicaoDoInvestidor(posicoes, investidorCpfCnpj, instrumento.instrumentoFinanceiroGuid);

    const operacaoCompraVendaCompromissadaRequest = buildCompromissadaRequest({
      quantidade,
      operacaoCodigo: instrumento.codigo,
      precoUnitario,
      custodianteDestinoId,
      contaDepositoDestino,
      indexador,
      indexadorPorcentagem,
      taxaDeJuros: taxaDeJurosFixo,
      dataDeRetorno,
      custodianteOrigemCnpj: custodianteLogado.cnpj,
      investidorCpfCnpjLimpo: cpfCnpjLimpo,
      contaDepositoOrigem: investidorOrigem?.investidor.contaDeposito,
      custodianteDestinoCnpj: cnpjCustodianteDestino,
    });

    postOperacaoCompraVendaCompromissada(dispatch, instrumento.instrumentoFinanceiroGuid, [operacaoCompraVendaCompromissadaRequest], true)
      .then(() => setFinished(true))
      .catch(() => {})
      .finally(() => setSubmitting(false));
  };

  return (
    <div style={{ marginRight: 40 }}>
      <Typography variant="h1">{'Operação Compromissada'}</Typography>
      <LgSpace />
      {finished ? (
        <Grid container direction="column" justify="center" alignItems="center">
          <div className={classes.iconWrapper}>
            <CheckCircle className={classes.icon} color="secondary" />
          </div>
          <SmSpace />
          <Typography variant="h3">{`Cadastro de movimentação concluída com sucesso!`}</Typography>
          <MdSpace />
          <Button onClick={() => history.push('/operacoes/gerenciar/cadastro')} variant="contained" color="primary">
            {'Voltar para a página inicial'}
          </Button>
          <MdSpace />
        </Grid>
      ) : (
        <>
          {inadimplido && (
            <>
              <SmSpace />
              <div className={genericClasses.errorToast}>
                <Cancel style={{ marginRight: 10 }} />
                <div style={{ display: 'flex', flex: 1 }}>
                  <Typography variant="body2">
                    {'Este Instrumento está Inadimplido, ao continuar com a Movimentação você declara ciência.'}
                  </Typography>
                </div>
              </div>
            </>
          )}
          <Section title={'Caracteristicas da Movimentação'} defaultGrid>
            <Grid container alignItems="flex-end" spacing={1}>
              <Grid item xs>
                <TextField
                  label={'Código do IF objeto da compromissada'}
                  value={codigoInstrumento}
                  onChange={(value) => setCodigoInstrumento(value ?? '')}
                  helperText={instrumento ? `${instrumento.numeroEmissao} - ${instrumento.razaoSocialEmissor}` : ''}
                />
              </Grid>
              <Grid item>
                <IconButton
                  color="primary"
                  onClick={buscarInstrumentoPorCodigo}
                  disabled={!codigoInstrumento.trim() || buscandoInstrumento}
                >
                  {buscandoInstrumento ? <CircularProgress size={24} /> : <Search />}
                </IconButton>
              </Grid>
            </Grid>
            <TextField
              label="CPF/CNPJ do investidor origem"
              value={investidorCpfCnpj}
              onChange={(value) => updateMaskInvestidorCpfCnpj(value ?? '')}
              error={!!investidorCpfCnpj && !investidorCpfCnpjValido}
              errorMessage={'CPF/CNPJ inválido'}
            />
          
          </Section>
          <Section title={'Informações financeiras'} defaultGrid>
          <Typography variant="h6">{'A custódia é própria'}</Typography>
          <RadioButton
              options={booleanOptions}
              value={custodiaPropria ? BooleanValue.Yes : BooleanValue.No}
              onChange={(value: string) => {
                setCustodiaPropria(value === BooleanValue.Yes)
              }}
              inline
            />
         <Grid container alignItems="flex-end" spacing={1}>
              <Grid item xs>
                <TextField
                  label="CNPJ Custodiante de Destino"
                  value={cnpjCustodianteDestino}
                  onChange={(value) => updateCnpjCustodianteDestino(value ?? '')}
                  error={!!cnpjCustodianteDestino && !cnpjCustodianteDestinoValido}
                  errorMessage={'CNPJ inválido'}
                  disabled={custodiaPropria}
                  helperText={custodianteDestinoRazaoSocial || ''}
                />
              </Grid>
              <Grid item>
                <IconButton
                  color="primary"
                  onClick={buscarCustodiantePorCnpj}
                  disabled={!cnpjCustodianteDestinoValido || buscandoCustodiante || custodiaPropria}
                >
                  {buscandoCustodiante ? <CircularProgress size={24} /> : <Search />}
                </IconButton>
              </Grid>
            </Grid>
          <NumberField
              label="Quantidade"
              value={quantidade}
              onChange={(value) => setQuantidade(value ?? 0)}
              required
              disabled={!investidorCpfCnpjValido}
              numberType={getQuantityType(instrumento?.tipoInstrumentoFinanceiro)}
              helperText={descreverQuantidadeDisponivel(quantidadeMax)}
              error={!!erroDaQuantidade}
              errorMessage={erroDaQuantidade}
            />
            <NumberField
              label="Preço unitário inicial"
              value={precoUnitario}
              onChange={(value) => setPrecoUnitario(value ?? 0)}
              required
              numberType="eightDecimals"
            />
            <DatePicker
              value={dataDeRetorno}
              label="Data de Retorno"
              required
              minDate={hoje}
              minDateMessage={MENSAGEM_DATA_DE_RETORNO_RETROATIVA}
              onChange={(value) => setDataRetorno(value ?? null)}
            />
            <Dropdown
              label="Indexador"
              value={indexador ?? ''}
              options={indexadorRemuneracaoOptions}
              onChange={setIndexador}
              required
            />
              <NumberField
              label='Porcentagem do indexador %'
              value={indexadorPorcentagem}
              onChange={(value) => {setPorcentagemIndexador(value?? 0)}}
              numberType="eightDecimals"
              adornment={'%'}
              adornmentPosition={'end'}
            />            
             <NumberField
              label='Taxa de Juros Fixo/Spread %'
              value={taxaDeJurosFixo}
              onChange={(value) => {setTaxaDeJurosFixo(value?? 0)}}
              numberType="eightDecimals"
              adornment={'%'}
              adornmentPosition={'end'}
            />            
          </Section>
          <Section title={'Custódia Destino'}  defaultGrid>
            <TextField
              label="Conta Depósito do Comprador *"
              value={contaDepositoDestino}
              onChange={(value) => updateContaDeposito(value)}
              error={!!contaDepositoDestino && !contaDepositoDestinoValido}
              errorMessage={'Campo inválido'}
            />
          </Section>
          <Grid container justify="center">
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                disabled={!podeComandar || submitting}
                onClick={handleSubmit}
              >
                {'Comandar Movimentação'}
              </Button>
              <LgSpace />
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};

export default CadastrarOperacaoCompraVendaCompromissada;
